import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PricingCTA from './PricingCTA';

function About() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    if (isLoaded) {
      console.log('Simulating scroll to portal section');
      const portalSection = document.querySelector('#portal-section');
      if (portalSection) {
        let start = null;
        const duration = 2000; // 2 seconds to scroll

        const scrollToPortal = (timestamp) => {
          if (!start) start = timestamp;
          const elapsed = timestamp - start;
          const progress = Math.min(elapsed / duration, 1);
          window.scrollTo(0, progress * portalSection.offsetTop);

          if (progress < 1) {
            requestAnimationFrame(scrollToPortal);
          }
        };

        requestAnimationFrame(scrollToPortal);
      }
    }
  }, [isLoaded]);

  return (
    <div className="relative bg-black text-white py-12 px-4 overflow-auto" style={{ height: '100vh' }}>
      <div className="max-w-6xl mx-auto relative">
        {/* Hero Section - Centered */}
        <div className="text-center mb-32 space-y-8 mt-16 animate-fade-in">
          <h1 className="text-7xl font-light tracking-wider mb-4 bg-gradient-to-r from-white via-gray-200 to-gray-400 inline-block text-transparent bg-clip-text animate-fade-in">
            About Orchestra
          </h1>
          <div className="flex justify-center">
            <div className="h-1 w-32 bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 rounded-full mb-6"></div>
          </div>
          {/* Mission Card */}
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-gray-900/80 via-gray-800/80 to-black/80 rounded-2xl shadow-lg border border-blue-900/40 p-8 mt-8 mb-4 backdrop-blur-md">
            <p className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-indigo-200 to-purple-300 mb-6 tracking-wide">
              Our mission: Build a dual-use, unified camera network to:
            </p>
            <ul className="space-y-4 pl-2">
              <li className="flex items-center gap-3 group">
                <span className="inline-block w-4 h-4 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full group-hover:scale-125 transition-transform"></span>
                <span className="text-lg text-gray-100 group-hover:text-blue-300 transition-colors font-light drop-shadow">
                  Prevent the next terrorist attack
                </span>
              </li>
              <li className="flex items-center gap-3 group">
                <span className="inline-block w-4 h-4 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full group-hover:scale-125 transition-transform"></span>
                <span className="text-lg text-gray-100 group-hover:text-blue-300 transition-colors font-light drop-shadow">
                  Record, analyze, and predict the physical world.
                </span>
              </li>
            </ul>
          </div>

        </div>

        {/* Ensemble Section - Left aligned with decorative element */}
        <section className="mb-32 transform hover:scale-[1.02] transition-all duration-500 relative">
          <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-blue-400 via-indigo-500 to-purple-500 rounded-full"></div>
          <div className="ml-8">
            <h2 className="text-5xl font-light tracking-wider mb-6 bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 inline-block text-transparent bg-clip-text">
              Ensemble
            </h2>
            <div className="max-w-3xl">
              <p className="text-3xl font-light italic mb-4 bg-gradient-to-r from-blue-300 via-indigo-300 to-purple-300 inline-block text-transparent bg-clip-text transform hover:scale-105 transition-transform duration-300">
                Without sound, there is no music.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Our proprietary camera network deployed across San Francisco.
              </p>
            </div>
          </div>
        </section>

        {/* Conductor Section - Right aligned with decorative element */}
        <section className="mb-32 transform hover:scale-[1.02] transition-all duration-500 relative text-right">
          <div className="absolute right-0 top-0 w-1 h-full bg-gradient-to-b from-orange-400 via-pink-500 to-rose-500 rounded-full"></div>
          <div className="mr-8">
            <h2 className="text-5xl font-light tracking-wider mb-6 bg-gradient-to-r from-orange-400 via-pink-500 to-rose-500 inline-block text-transparent bg-clip-text">
              Conductor
            </h2>
            <div className="max-w-3xl ml-auto">
              <p className="text-3xl font-light italic mb-4 bg-gradient-to-r from-orange-300 via-pink-300 to-rose-300 inline-block text-transparent bg-clip-text transform hover:scale-105 transition-transform duration-300">
                Without vision, there is no harmony.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                Our computer vision engine transforms raw footage into actionable insights.
              </p>
            </div>
          </div>
        </section>

        {/* Symphonies Section - Centered */}
        <section className="mb-32">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light tracking-wider mb-8 bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 inline-block text-transparent bg-clip-text">
              Symphonies
            </h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-3xl font-light italic bg-gradient-to-r from-green-300 via-emerald-300 to-teal-300 inline-block text-transparent bg-clip-text transform hover:scale-105 transition-transform duration-300">
                Now Playing: Movement I
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: 'Archangel', path: '/archangel', desc: 'A unified camera network solving major crimes in San Francisco' },
              { title: 'Veritas', path: '/veritas', desc: 'Video evidence that eliminates fraudulent car crash claims' }
            ].map(({ title, path, desc }) => (
              <Link
                to={path}
                key={title}
                className="group bg-gradient-to-br from-gray-900/50 via-gray-900/30 to-black/50 p-10 rounded-2xl 
                          hover:bg-gradient-to-br hover:from-green-900/20 hover:via-emerald-900/20 hover:to-teal-900/20 
                          transition-all duration-500 transform hover:scale-105 backdrop-blur-sm border border-gray-800/50"
              >
                <h3 className="text-2xl font-light mb-6 group-hover:text-green-300 transition-colors duration-300">{title}</h3>
                <p className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                  {desc}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* Add PricingCTA before closing divs */}
        {/* <PricingCTA className="mb-24" /> */}
        {/* Footer */}
        <footer className="bg-black border-t border-white/10 py-8 md:py-12 snap-start">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8">
              <nav className="mb-6 md:mb-0">
                <ul className="flex flex-wrap justify-center md:justify-start gap-6 md:gap-8">
                  <li>
                    <span className="text-white/60 hover:text-white/90 transition-colors text-sm cursor-not-allowed">
                      Docs
                      <span className="ml-2 text-xs text-white/40">(Coming Soon)</span>
                    </span>
                  </li>
                  <li>
                    <a
                      href="#contact-section"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('contact-section').scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-white/60 hover:text-white/90 transition-colors text-sm"
                    >
                      Book a Call
                    </a>
                  </li>
                  <li>
                    <Link
                      to="/about"
                      className="text-white/60 hover:text-white/90 transition-colors text-sm"
                    >
                      About
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy"
                      className="text-white/60 hover:text-white/90 transition-colors text-sm"
                    >
                      Privacy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/careers"
                      className="text-white/60 hover:text-white/90 transition-colors text-sm"
                    >
                      Careers
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-white/40 text-sm mb-4 md:mb-0">
                © 2025 Orchestra, Inc. – Built in San Francisco
              </p>
              <a
                href="mailto:drake@orchestraos.com"
                className="text-white/60 hover:text-orange-400 transition-colors text-sm group relative"
                title="drake@orchestraos.com"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default About; 